import { Box, Grid, useTheme } from '@mui/material';
import {useWindowWidth} from '@react-hook/window-size';
import styled, {css, keyframes} from 'styled-components';


/* ── Animations ── */
const pulseRing = keyframes`
    0%   { box-shadow: 0 0 0 0 rgba(59,130,246,0.45); }
    70%  { box-shadow: 0 0 0 8px rgba(59,130,246,0); }
    100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); }
`;

/* ════════════════════════════════
   MOBILE  –  Circle stepper
   ════════════════════════════════ */
const StyleCircle = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    cursor: ${props => props.validate ? 'pointer' : 'default'};

    /* ── connector line ── */
    .connector{
        position: absolute;
        top: 22px;
        left: calc(50% + 20px);
        right: calc(-50% + 20px);
        height: 3px;
        border-radius: 2px;
        background: ${props => props.connectorColor};
        transition: background 0.3s ease;
        z-index: 0;

        @media (min-width: 480px) {
            top: 24px;
            left: calc(50% + 24px);
            right: calc(-50% + 24px);
        }
    }

    /* ── circle ── */
    .box-circle{
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        z-index: 1;
        transition: all 0.3s cubic-bezier(.4,0,.2,1);
        background: ${props => props.circleBg};
        color: ${props => props.circleColor};
        border: 3px solid ${props => props.borderColor};
        box-shadow: ${props => props.selected
            ? '0 4px 14px rgba(59,130,246,0.35)'
            : '0 1px 4px rgba(0,0,0,0.12)'};
        ${props => props.selected && css`
            animation: ${pulseRing} 2s ease-out infinite;
        `}

        @media (min-width: 480px) {
            width: 48px;
            height: 48px;
        }
    }
    .box-circle svg {
        font-size: 22px;
        @media (min-width: 480px) {
            font-size: 24px;
        }
    }

    /* ── label ── */
    .title{
        margin-top: 8px;
        text-align: center;
        font-weight: ${props => props.selected ? '700' : '500'};
        font-size: 10px;
        line-height: 1.25;
        color: ${props => props.labelColor};
        word-break: break-word;
        padding: 0 2px;
        transition: all 0.3s ease;
        max-width: 90px;

        @media (min-width: 480px) {
            font-size: 11px;
            max-width: 110px;
        }
        @media (min-width: 768px) {
            font-size: 12px;
            max-width: 130px;
        }
    }
`;

/* ════════════════════════════════
   DESKTOP  –  Card stepper
   ════════════════════════════════ */
const StylesBox = styled.div`
    flex: 1;
    min-width: 0;

    .box-step{
        cursor: ${props => props.validate ? 'pointer' : 'default'};
        color: ${props => props.color};
        background: ${props => props.background};
        text-decoration: none;
        position: relative;
        height: 72px;
        padding: 0 12px;
        line-height: 20px;
        display: flex;
        align-items: center;
        border-radius: 14px;
        margin-right: 10px;
        transition: all 0.3s cubic-bezier(.4,0,.2,1);
        border: 2px solid ${props => props.borderColor || 'transparent'};
        overflow: hidden;

        @media (min-width: 1200px) {
            height: 80px;
            padding: 0 16px;
            margin-right: 14px;
        }

        /* accent bar */
        &::before{
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            border-radius: 14px 0 0 14px;
            background: ${props => props.accentColor || 'transparent'};
            transition: width 0.3s ease;
        }

        .box-header{
            text-align: left;
            overflow: hidden;
            .title{
                font-weight: 700;
                font-size: 13px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                @media (min-width: 1200px) {
                    font-size: 14px;
                }
            }
            .subtitle{
                font-size: 11px;
                opacity: 0.75;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                margin-top: 2px;
                @media (min-width: 1200px) {
                    font-size: 12px;
                }
            }
        }

        ${props => props.validate && css`
            &:hover{
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(59,130,246,0.25);
                border-color: #3B82F6;
                &::before{ width: 5px; background: #3B82F6; }
            }
        `}
    }
`;


/* ════════════════════════════════
   Step Number Badge (desktop)
   ════════════════════════════════ */
const stepNumberSx = (isDarkMode, selected, validate) => {
    const base = {
        width: 28, height: 28, borderRadius: '50%',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        fontSize: 13, fontWeight: 700,
        transition: 'all 0.3s ease',
        flexShrink: 0,
    };
    if (selected) {
        return { ...base, background: 'linear-gradient(135deg, #3B82F6, #6366F1)', color: '#fff', boxShadow: '0 2px 8px rgba(59,130,246,0.4)' };
    }
    if (validate) {
        return { ...base, background: isDarkMode ? '#1e3a5f' : '#e6f0ff', color: isDarkMode ? '#90caf9' : '#0364ff' };
    }
    return { ...base, background: isDarkMode ? '#334155' : '#e2e8f0', color: isDarkMode ? '#94a3b8' : '#64748b' };
};


function StepTitle({
    title,
    index,
    subtitle,
    selected,
    validate,
    className,
    onClick,
    icon,
    isLast,
}){
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const onlyWidth = useWindowWidth();

    /* ── palette ── */
    const accent = '#3B82F6';

    let circleBg, circleColor, borderColor, labelColor, connectorColor;
    let bgBox, colorBox, borderBox, accentBar;

    if (selected) {
        circleBg    = 'linear-gradient(135deg, #3B82F6, #6366F1)';
        circleColor = '#fff';
        borderColor = 'transparent';
        labelColor  = isDarkMode ? '#e0e0e0' : '#1e293b';
        connectorColor = isDarkMode ? '#475569' : '#cbd5e1';

        bgBox      = isDarkMode ? '#1e293b' : '#f0f6ff';
        colorBox   = isDarkMode ? '#e2e8f0' : '#1e293b';
        borderBox  = accent;
        accentBar  = 'linear-gradient(180deg, #3B82F6, #6366F1)';
    } else if (validate) {
        circleBg    = isDarkMode ? '#1e3a5f' : '#e6f0ff';
        circleColor = isDarkMode ? '#90caf9' : '#0364ff';
        borderColor = isDarkMode ? '#3B82F6' : '#93c5fd';
        labelColor  = isDarkMode ? '#a5b4c8' : '#475569';
        connectorColor = isDarkMode ? '#3B82F6' : '#93c5fd';

        bgBox      = isDarkMode ? '#1e293b' : '#f0f6ff';
        colorBox   = isDarkMode ? '#90caf9' : '#0364ff';
        borderBox  = isDarkMode ? '#3B82F644' : '#93c5fd44';
        accentBar  = '#3B82F6';
    } else {
        circleBg    = isDarkMode ? '#1e293b' : '#f1f5f9';
        circleColor = isDarkMode ? '#64748b' : '#94a3b8';
        borderColor = isDarkMode ? '#334155' : '#e2e8f0';
        labelColor  = isDarkMode ? '#64748b' : '#94a3b8';
        connectorColor = isDarkMode ? '#334155' : '#e2e8f0';

        bgBox      = isDarkMode ? '#1e293b' : '#f8fafc';
        colorBox   = isDarkMode ? '#64748b' : '#94a3b8';
        borderBox  = 'transparent';
        accentBar  = 'transparent';
    }

    const onClickStep = () => {
        if (validate) onClick();
    };

    /* ─── Mobile ─── */
    if (onlyWidth < 992) {
        return (
            <StyleCircle
                className={className}
                circleBg={validate && !selected ? circleBg : 'transparent'}
                circleColor={circleColor}
                borderColor={borderColor}
                labelColor={labelColor}
                connectorColor={connectorColor}
                selected={selected}
                validate={validate}
                onClick={onClickStep}
                aria-hidden="true"
            >
                {!isLast && <div className="connector" />}
                <div className="box-circle" style={selected ? { background: circleBg } : undefined}>
                    {icon}
                </div>
                <div className="title">{title}</div>
            </StyleCircle>
        );
    }

    /* ─── Desktop ─── */
    return (
        <StylesBox
            className={className}
            color={colorBox}
            background={bgBox}
            validate={validate}
            borderColor={borderBox}
            accentColor={accentBar}
        >
            <Box
                className="box-step"
                onClick={onClickStep}
                aria-hidden="true"
                sx={{
                    boxShadow: selected
                        ? '0 4px 16px rgba(59,130,246,0.2)'
                        : '0 1px 4px rgba(0,0,0,0.06)',
                }}
            >
                <Grid container spacing={1} wrap="nowrap" alignItems="center">
                    <Grid item display="flex" justifyContent="center" alignItems="center" sx={{ minWidth: { xs: 36, lg: 46 } }}>
                        <Box sx={{
                            width: { xs: 34, lg: 42 },
                            height: { xs: 34, lg: 42 },
                            borderRadius: '50%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: selected
                                ? (isDarkMode ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)')
                                : (isDarkMode ? 'rgba(148,163,184,0.1)' : 'rgba(0,0,0,0.04)'),
                            transition: 'all 0.3s ease',
                            '& svg': { fontSize: { xs: 18, lg: 22 }, opacity: selected ? 1 : 0.7 },
                        }}>
                            {icon}
                        </Box>
                    </Grid>
                    <Grid item xs display="flex" justifyContent="start" alignItems="center" sx={{ overflow: 'hidden' }}>
                        <div className="box-header">
                            <Box className="title">{title}</Box>
                            {subtitle && <div className="subtitle">{subtitle}</div>}
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </StylesBox>
    );
}

StepTitle.defaultProps = {
    title: '',
    subtitle: '',
    selected: false,
    validate: false,
    isLast: false,
    onClick: () => {},
};

export default StepTitle;